import { useState, useEffect, useCallback } from 'react';
import { X, ArrowRight, Gift, CheckCircle, GraduationCap, Star, Clock, Heart, Sparkles, Users } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { validateEmail, normalizeEmail } from '../utils/emailValidation';
import axios from 'axios';
import { BACKEND_URL } from '../utils/api';

const API = `${BACKEND_URL}/api`;

// Bump version to force all users to see new graduation popup
const POPUP_DESIGN_VERSION = '3.0-grad';

/**
 * SmartPopup - Graduation Gift Portfolio A/B Testing
 * 4 entry variants + 1 exit intent — all pushing gift-portfolio
 */
export default function SmartPopup() {
  const [entryPopupVisible, setEntryPopupVisible] = useState(false);
  const [exitPopupVisible, setExitPopupVisible] = useState(false);
  const [hasShownEntry, setHasShownEntry] = useState(false);
  const [hasShownExit, setHasShownExit] = useState(false);
  const [abVariant, setAbVariant] = useState('A');
  const [formData, setFormData] = useState({ name: '', email: '', gradName: '', gradEmail: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [exitFormSubmitted, setExitFormSubmitted] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  // ES if (a) on /es/* route, OR (b) i18n locale is Spanish (covers users who
  // toggled language on an EN domain page or are mid-route-transition).
  const i18nLang = (i18n.language || 'en').split('-')[0];
  const es = location.pathname.startsWith('/es') || i18nLang === 'es';

  // Close any open popup the moment the route changes so it re-renders in the
  // correct locale. Without this, a popup opened on `/` and surviving a SPA
  // navigation to `/es` would still show English text.
  useEffect(() => {
    setEntryPopupVisible(false);
    setExitPopupVisible(false);
  }, [location.pathname, i18nLang]);

  const excludedPaths = [
    '/admin', '/contest/enter', '/analyze',
    '/warm-handoffs', '/gift-portfolio', '/es/regalo-portafolio',
    '/2026-kentucky-derby',
    '/partner/hq', '/partner/install', '/partner/',
    '/affiliate/dashboard', '/affiliate/login', '/affiliates/login',
    '/login', '/signin', '/social-login',
    '/client-portal', '/client/', '/ppc-portal', '/client-pay',
    '/hosting', '/enterprise', '/es/empresas',
    '/onboarding', '/winner-onboarding', '/runnerup-onboarding', '/gift-onboard',
    '/agency/register', '/pay/', '/domain-checkout',
  ];

  useEffect(() => {
    const storedVersion = localStorage.getItem('popupDesignVersion');
    if (storedVersion !== POPUP_DESIGN_VERSION) {
      localStorage.removeItem('popupAbVariant');
      localStorage.setItem('popupDesignVersion', POPUP_DESIGN_VERSION);
    }
    
    const storedVariant = localStorage.getItem('popupAbVariant');
    if (storedVariant && ['A','B','C','D'].includes(storedVariant)) {
      setAbVariant(storedVariant);
    } else {
      const variants = ['A', 'B', 'C', 'D'];
      const randomVariant = variants[Math.floor(Math.random() * variants.length)];
      localStorage.setItem('popupAbVariant', randomVariant);
      setAbVariant(randomVariant);
    }
  }, []);

  const shouldShowEntryPopup = useCallback(() => {
    if (excludedPaths.some(path => location.pathname.startsWith(path))) return false;
    const shownThisSession = sessionStorage.getItem('entryPopupShown');
    return !shownThisSession;
  }, [location.pathname]);

  const shouldShowExitPopup = useCallback(() => {
    if (excludedPaths.some(path => location.pathname.startsWith(path))) return false;
    const shownThisSession = sessionStorage.getItem('exitPopupShown');
    return !shownThisSession;
  }, [location.pathname]);

  const trackPopupEvent = useCallback(async (event, variant, data = {}) => {
    try {
      await axios.post(`${API}/track/popup-event`, {
        event, variant: `grad-${variant}`, page: location.pathname, timestamp: new Date().toISOString(), ...data
      });
    } catch (e) {}
  }, [location.pathname]);

  useEffect(() => {
    if (!shouldShowEntryPopup() || hasShownEntry) return;
    const timer = setTimeout(() => {
      setEntryPopupVisible(true);
      setHasShownEntry(true);
      sessionStorage.setItem('entryPopupShown', 'true');
      trackPopupEvent('entry_impression', abVariant);
    }, 4000);
    return () => clearTimeout(timer);
  }, [shouldShowEntryPopup, hasShownEntry, abVariant, trackPopupEvent]);

  useEffect(() => {
    if (!shouldShowExitPopup() || hasShownExit) return;
    const handleMouseLeave = (e) => {
      if (e.clientY <= 10 && !hasShownExit && !entryPopupVisible) {
        setExitPopupVisible(true);
        setHasShownExit(true);
        sessionStorage.setItem('exitPopupShown', 'true');
        trackPopupEvent('exit_impression', abVariant);
      }
    };
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [hasShownExit, shouldShowExitPopup, entryPopupVisible, abVariant, trackPopupEvent]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') { setEntryPopupVisible(false); setExitPopupVisible(false); }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const goToGift = () => {
    trackPopupEvent('entry_cta_click', abVariant);
    setEntryPopupVisible(false);
    navigate(es ? '/es/regalo-portafolio' : '/gift-portfolio');
  };

  const handleExitSubmit = async (e) => {
    e.preventDefault();
    const emailErr = validateEmail(formData.email);
    if (emailErr) { alert(emailErr); return; }
    const cleanEmail = normalizeEmail(formData.email);
    try {
      await axios.post(`${API}/leads/popup-capture`, {
        name: formData.name, email: cleanEmail,
        source: 'exit_popup_grad', variant: `grad-${abVariant}`, page: location.pathname,
        notes: `Grad name: ${formData.gradName}, Grad email: ${formData.gradEmail}`
      });
      trackPopupEvent('exit_conversion', abVariant);
      setExitFormSubmitted(true);
      setTimeout(() => setExitPopupVisible(false), 2500);
    } catch (err) {}
  };

  // ── VARIANT A: Urgency + Countdown ──
  const variantA = () => (
    <>
      <div className="bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-900 p-6 rounded-t-xl text-center relative overflow-hidden">
        <div className="absolute top-0 right-0 bg-red-500 text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg">{es ? 'TIEMPO LIMITADO' : 'LIMITED TIME'}</div>
        <GraduationCap className="h-12 w-12 text-yellow-300 mx-auto mb-3" />
        <h2 className="text-xl font-bold text-white mb-1">
          {es ? 'La Graduación Está a Semanas' : 'Graduation Is Weeks Away'}
        </h2>
        <p className="text-purple-200 text-sm">
          {es ? 'Deles la ventaja profesional que nadie más les dará' : 'Give them the career advantage no one else will'}
        </p>
      </div>
      <div className="p-6 bg-white rounded-b-xl">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-center">
          <p className="text-sm font-semibold text-amber-800">{es ? 'Especial Clase 2026' : 'Class of 2026 Special'}</p>
          <p className="text-2xl font-bold text-amber-900">$19.99<span className="text-sm font-normal">/mo</span></p>
          <p className="text-xs text-amber-600">{es ? 'Portafolio profesional + dominio propio + herramientas IA' : 'Professional portfolio + custom domain + AI tools'}</p>
        </div>
        <div className="space-y-2 mb-4">
          {(es ? ['Dominio propio (SuNombre.com)', 'Portafolio profesional en 48hrs', 'Currículum y cartas con IA', 'Tarjetas de presentación QR'] : ['Custom domain (TheirName.com)', 'Professional portfolio site in 48hrs', 'AI-powered resume & cover letters', 'QR business cards (Avery print-ready)']).map((f, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-slate-700">
              <CheckCircle className="h-4 w-4 text-indigo-500 shrink-0" /> {f}
            </div>
          ))}
        </div>
        <Button onClick={goToGift} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-5 text-base font-semibold rounded-lg" data-testid="popup-grad-cta">
          <Gift className="h-4 w-4 mr-2" /> {es ? 'Ver Paquetes de Regalo' : 'See Gift Packages'}
        </Button>
        <p className="text-xs text-slate-400 text-center mt-2">{es ? 'Sin cargos de instalación. Cancele cuando quiera.' : 'No setup fees. Cancel anytime.'}</p>
      </div>
    </>
  );

  // ── VARIANT B: Emotional / Parent Angle ──
  const variantB = () => (
    <>
      <div className="bg-gradient-to-br from-slate-900 to-blue-900 p-6 rounded-t-xl text-center">
        <Heart className="h-10 w-10 text-red-400 mx-auto mb-3" />
        <h2 className="text-xl font-bold text-white mb-1">
          {es ? 'El Regalo Que Les Consigue Trabajo' : 'The Gift That Gets Them Hired'}
        </h2>
        <p className="text-blue-200 text-sm">
          {es ? 'Mientras otros graduados envían currículums en papel, el suyo destaca en línea' : 'While other grads send paper resumes, yours stands out online'}
        </p>
      </div>
      <div className="p-6 bg-white rounded-b-xl">
        <div className="mb-4">
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg mb-2">
            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center shrink-0">
              <X className="h-4 w-4 text-red-500" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-800">{es ? 'Efectivo o tarjetas de regalo' : 'Cash or gift cards'}</p>
              <p className="text-xs text-slate-500">{es ? 'Se gasta en una semana. Se olvida.' : 'Spent in a week. Forgotten.'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
            <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center shrink-0">
              <CheckCircle className="h-4 w-4 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-800">{es ? 'Un portafolio profesional' : 'A professional portfolio'}</p>
              <p className="text-xs text-indigo-600 font-medium">{es ? 'Consigue entrevistas. Construye carreras.' : 'Gets interviews. Builds careers.'}</p>
            </div>
          </div>
        </div>
        <Button onClick={goToGift} className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white py-5 text-base font-semibold rounded-lg" data-testid="popup-grad-cta">
          {es ? 'Deles Una Ventaja' : 'Give Them a Head Start'} <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
        <p className="text-xs text-slate-400 text-center mt-2">{es ? 'Desde $19.99/mes. Listo en 48 horas.' : 'Starting at $19.99/mo. Setup in 48 hours.'}</p>
      </div>
    </>
  );

  // ── VARIANT C: Social Proof / FOMO ──
  const variantC = () => (
    <>
      <div className="bg-gradient-to-br from-emerald-900 to-teal-900 p-6 rounded-t-xl text-center">
        <div className="flex items-center justify-center gap-1 mb-3">
          {[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />)}
        </div>
        <h2 className="text-xl font-bold text-white mb-1">
          {es ? 'Regalo de Graduación #1 del 2026' : '#1 Graduation Gift for 2026'}
        </h2>
        <p className="text-emerald-200 text-sm">
          {es ? 'Padres y familias están eligiendo esto en vez de efectivo' : 'Parents & families are choosing this over cash'}
        </p>
      </div>
      <div className="p-6 bg-white rounded-b-xl">
        <div className="flex items-center gap-3 bg-emerald-50 rounded-lg p-3 mb-4 border border-emerald-200">
          <Users className="h-5 w-5 text-emerald-600 shrink-0" />
          <p className="text-sm text-emerald-800">{es ? <><strong>327 familias</strong> ya ordenaron para la Clase del 2026</> : <><strong>327 families</strong> have already ordered for Class of 2026</>}</p>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-4 text-center">
          <div className="bg-slate-50 rounded-lg p-2">
            <div className="text-lg font-bold text-slate-900">48hr</div>
            <div className="text-[10px] text-slate-500">{es ? 'Tiempo' : 'Setup Time'}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-2">
            <div className="text-lg font-bold text-slate-900">$19.99</div>
            <div className="text-[10px] text-slate-500">{es ? 'Por Mes' : 'Per Month'}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-2">
            <div className="text-lg font-bold text-slate-900">4.9/5</div>
            <div className="text-[10px] text-slate-500">{es ? 'Calificación' : 'Rating'}</div>
          </div>
        </div>
        <Button onClick={goToGift} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-5 text-base font-semibold rounded-lg" data-testid="popup-grad-cta">
          <GraduationCap className="h-4 w-4 mr-2" /> {es ? 'Ordene Antes de la Graduación' : 'Order Before Graduation'}
        </Button>
        <p className="text-xs text-slate-400 text-center mt-2">{es ? 'Opción de envoltura de regalo disponible.' : 'Gift wrap option available. Ships as a printed card.'}</p>
      </div>
    </>
  );

  // ── VARIANT D: Direct / No-Nonsense ──
  const variantD = () => (
    <>
      <div className="bg-slate-900 p-6 rounded-t-xl text-center">
        <Sparkles className="h-10 w-10 text-blue-400 mx-auto mb-3" />
        <h2 className="text-xl font-bold text-white mb-1">
          {es ? 'Van a Enviar 100 Resumes.' : "They'll Send 100 Resumes."}
        </h2>
        <p className="text-lg text-blue-300 font-semibold">
          {es ? 'Solo los que tienen portafolio reciben llamadas.' : 'Only the ones with a portfolio get callbacks.'}
        </p>
      </div>
      <div className="p-6 bg-white rounded-b-xl">
        <p className="text-sm text-slate-600 mb-4">
          {es ? <>Los reclutadores pasan <strong>6 segundos</strong> en un resume. Un portafolio personal con dominio propio los hace detenerse y mirar.</> : <>Hiring managers spend <strong>6 seconds</strong> on a resume. A personal portfolio with a custom domain makes them stop and look.</>}
        </p>
        <div className="bg-blue-50 rounded-lg p-4 mb-4 border border-blue-100">
          <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide mb-2">{es ? 'Lo que reciben' : 'What they get'}</p>
          <div className="grid grid-cols-2 gap-2 text-xs text-blue-700">
            <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3" /> {es ? 'Dominio SuNombre.com' : 'TheirName.com domain'}</span>
            <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3" /> {es ? 'Sitio portafolio' : 'Portfolio website'}</span>
            <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3" /> {es ? 'Herramientas IA de resume' : 'AI resume tools'}</span>
            <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3" /> {es ? 'Tarjeta digital' : 'Digital business card'}</span>
          </div>
        </div>
        <Button onClick={goToGift} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-5 text-base font-semibold rounded-lg" data-testid="popup-grad-cta">
          {es ? '$19.99/mes — Ver Opciones de Regalo' : '$19.99/mo — See Gift Options'} <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
        <div className="flex items-center justify-center gap-4 mt-3 text-xs text-slate-400">
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {es ? 'Listo en 48hrs' : 'Ready in 48hrs'}</span>
          <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3" /> {es ? 'Sin cargo inicial' : 'No setup fee'}</span>
        </div>
      </div>
    </>
  );

  const renderEntryPopupContent = () => {
    switch(abVariant) {
      case 'A': return variantA();
      case 'B': return variantB();
      case 'C': return variantC();
      case 'D': return variantD();
      default: return variantA();
    }
  };

  // ── EXIT INTENT: "Know a Grad?" lead capture ──
  const renderExitPopup = () => (
    <>
      <div className="bg-gradient-to-br from-indigo-900 to-purple-900 p-6 rounded-t-xl text-center">
        <GraduationCap className="h-10 w-10 text-yellow-300 mx-auto mb-3" />
        <h2 className="text-xl font-bold text-white mb-1">
          {es ? '¿Conoce a Alguien que se Gradúa?' : 'Know Someone Graduating?'}
        </h2>
        <p className="text-purple-200 text-sm">
          {es ? 'Envíele el regalo que lanza carreras — nosotros nos encargamos de todo' : "Send them the gift that launches careers — we'll set it all up"}
        </p>
      </div>
      <div className="p-6 bg-white rounded-b-xl">
        {!exitFormSubmitted ? (
          <form onSubmit={handleExitSubmit} className="space-y-3">
            <div className="bg-purple-50 rounded-lg p-3 mb-1 border border-purple-100">
              <p className="text-xs text-purple-800">{es ? <>Cuéntenos sobre el graduado y le enviaremos una vista previa personalizada de su portafolio — <strong>completamente gratis, sin compromiso.</strong></> : <>Tell us about the grad and we'll send them a personalized preview of their portfolio — <strong>completely free, no obligation.</strong></>}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-600 mb-1 block">{es ? 'Su Nombre' : 'Your Name'}</label>
                <Input type="text" placeholder={es ? 'Su nombre' : 'Your name'} value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="border-slate-200 text-sm" data-testid="exit-your-name" />
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">{es ? 'Su Email' : 'Your Email'}</label>
                <Input type="email" placeholder={es ? 'Su email' : 'Your email'} value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required className="border-slate-200 text-sm" data-testid="exit-your-email" />
              </div>
            </div>
            <div className="border-t border-slate-200 pt-3">
              <p className="text-xs font-medium text-slate-700 mb-2">{es ? '¿Quién se gradúa?' : "Who's graduating?"}</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-600 mb-1 block">{es ? 'Nombre del Graduado' : "Grad's Name"}</label>
                  <Input type="text" placeholder={es ? 'Su nombre' : 'Their name'} value={formData.gradName} onChange={(e) => setFormData({...formData, gradName: e.target.value})} required className="border-slate-200 text-sm" data-testid="exit-grad-name" />
                </div>
                <div>
                  <label className="text-xs text-slate-600 mb-1 block">{es ? 'Email del Graduado' : "Grad's Email"}</label>
                  <Input type="email" placeholder={es ? 'Su email' : 'Their email'} value={formData.gradEmail} onChange={(e) => setFormData({...formData, gradEmail: e.target.value})} className="border-slate-200 text-sm" data-testid="exit-grad-email" />
                </div>
              </div>
            </div>
            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 py-5 font-semibold" data-testid="exit-submit-btn">
              <Gift className="h-4 w-4 mr-2" />
              {es ? 'Enviar Vista Previa Gratis al Graduado' : 'Send Free Preview to the Grad'}
            </Button>
            <p className="text-xs text-slate-400 text-center">{es ? 'Sin spam. Sin compromiso. Solo una vista previa personalizada.' : 'No spam. No obligation. Just a personalized portfolio preview.'}</p>
          </form>
        ) : (
          <div className="text-center py-6">
            <CheckCircle className="h-14 w-14 text-indigo-600 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-slate-900">{es ? '¡Vista previa en camino!' : 'Preview on its way!'}</h3>
            <p className="text-slate-600 text-sm">{es ? 'Le enviaremos un adelanto de su portafolio' : "We'll email them a sneak peek of their portfolio"}</p>
          </div>
        )}
      </div>
    </>
  );

  if (!entryPopupVisible && !exitPopupVisible) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) { setEntryPopupVisible(false); setExitPopupVisible(false); }
      }}
      data-testid="smart-popup-overlay"
    >
      <div className="relative max-w-md w-full animate-in fade-in slide-in-from-bottom-4 duration-300">
        <button
          onClick={() => { setEntryPopupVisible(false); setExitPopupVisible(false); }}
          className="absolute -top-3 -right-3 bg-white rounded-full p-1.5 shadow-lg hover:bg-slate-100 transition-colors z-10"
          data-testid="popup-close-btn"
        >
          <X className="h-5 w-5 text-slate-600" />
        </button>
        <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
          {/* Mutually exclusive — exit-intent supersedes the entry popup if both
              flags happen to be true. Prevents the "stacked popups" bug. */}
          {exitPopupVisible
            ? renderExitPopup()
            : entryPopupVisible && renderEntryPopupContent()}
        </div>
      </div>
    </div>
  );
}
